import React, { useEffect, useState } from "react";
import "./MissionPopup.css";
import { useAuth } from "../../../../Security/AuthProvider";
import { useNotification } from "../../../../Utils/Commons/Notification/NotificationContext";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import "react-datepicker/dist/react-datepicker.css";

// Enregistrer la locale française
registerLocale("fr", fr);

const MissionPopup = ({ mission, onClose, onUpdate }) => {
  // Fonction pour formater la date au format YYYY-MM-DD pour les inputs
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };



  const [editedMission, setEditedMission] = useState({
    client_name: mission.client_name || '',
    adresse: mission.adresse || '',
    appartement: mission.appartement || '',
    operation_id: mission.operation_id || null,
    date: formatDateForInput(mission.date) || '',
    beginning: mission.beginning !== undefined ? mission.beginning : 0, // Intervalle 0 = 8h00 par défaut
    ending: mission.ending !== undefined ? mission.ending : 4, // Intervalle 4 = 9h00 par défaut
  });
  
  // State pour le DatePicker français
  const [selectedDate, setSelectedDate] = useState(
    mission.date ? new Date(mission.date) : null
  );
  const [comments, setComments] = useState(mission.comments || '');
  const [isSaving, setSaving] = useState(false);
  const [operations, setOperations] = useState([]);


  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();

  const getBackToHours = (timeSlot, baseHour = 8) => {
    const minutes = (timeSlot * 15) % 60;
    const hourOffset = Math.floor((timeSlot * 15) / 60);
    const hour = baseHour + hourOffset;
    return `${hour}h${minutes === 0 ? "00" : minutes}`;
  };

  // Fonction spécifique pour convertir les intervalles en heures FIXES (intervalle 0 = 8h00, intervalle 36 = 17h00)
  const intervalToHoursFixed = (interval) => {
    if (interval === undefined || interval === null) return "8h00";
    // Calcul direct : chaque intervalle = 15 minutes, intervalle 0 = 8h00
    const totalMinutes = interval * 15;
    const hours = 8 + Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h${minutes === 0 ? "00" : minutes < 10 ? "0" + minutes : minutes}`;
  };



  // Convertir les heures au format HH:MM vers des intervalles de 15 minutes
  const timeToInterval = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const totalMinutes = (hours - 8) * 60 + minutes; // Basé sur 8h00 = début de journée
    return Math.max(0, Math.floor(totalMinutes / 15));
  };

  // Convertir les intervalles vers le format HH:MM français (24h)
  const intervalToTime = (interval) => {
    if (interval === undefined || interval === null) return "08:00";
    const totalMinutes = interval * 15;
    const hours = Math.floor(totalMinutes / 60) + 8; // Ajouter 8h pour le décalage
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Validation des conflits d'horaires
  const checkTimeConflicts = async (date, beginning, ending, currentMissionId) => {
    try {
      const credentials = await getCredentials();

      const response = await fetch('/rdv/check-conflicts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: mission.agent_id,
          agent_name: mission.agent_name, // Fallback si agent_id n'existe pas
          date: date,
          beginning: beginning,
          ending: ending,
          excludeRdvId: currentMissionId,
          company_name: credentials.company_name,
          company_password: credentials.company_password,
        }),
      });

      if (!response.ok) {
        console.error('Erreur HTTP lors de la vérification des conflits:', response.status);
        return false;
      }

      const data = await response.json();
      return data.hasConflict;
    } catch (error) {
      console.error('Erreur lors de la vérification des conflits:', error);
      return false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    // Charger la liste des opérations
    const fetchOperations = async () => {
      try {
        const credentials = await getCredentials();
        const response = await fetch('/operations/get-all', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_name: credentials.company_name,
            company_password: credentials.company_password,
          }),
        });
        if (!response.ok) throw new Error('Erreur lors du chargement des opérations');
        const data = await response.json();
        setOperations(data);
        
        // Si l'opération actuelle existe, mettre à jour editedMission avec son ID
        if (mission.operation_id) {
          setEditedMission(prev => ({
            ...prev,
            operation_id: mission.operation_id
          }));
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    fetchOperations();
  }, [getCredentials, mission.operation_id]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedMission(prev => ({
      ...prev,
      [name]: name === 'operation_id' ? (value === '' ? null : value) : value
    }));
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      setEditedMission(prev => ({ ...prev, date: formattedDate }));
      
      // Vérifier les conflits si on a des heures définies
      if (editedMission.beginning !== undefined && editedMission.ending !== undefined && editedMission.beginning < editedMission.ending) {
        const hasConflict = await checkTimeConflicts(
          formattedDate, 
          editedMission.beginning, 
          editedMission.ending, 
          mission.id
        );
        
        if (hasConflict) {
          notifyError('Conflit d\'horaire détecté avec un autre rendez-vous à cette date');
        }
      }
    }
  };

  const handleTimeSliderChange = async (e) => {
    const { name, value } = e.target;
    const intervalValue = parseInt(value);
    
    let newValues = { ...editedMission };
    
    if (name === 'startTimeSlider') {
      // Limites FIXES : 8h00 (0) à 16h45 (35)
      if (intervalValue < 0 || intervalValue > 35) {
        notifyError('L\'heure de début doit être entre 8h00 et 16h45');
        return;
      }
      newValues.beginning = intervalValue;
    } else if (name === 'endTimeSlider') {
      // Limites FIXES : 8h15 (1) à 17h00 (36)
      if (intervalValue < 1 || intervalValue > 36) {
        notifyError('L\'heure de fin doit être entre 8h15 et 17h00');
        return;
      }
      newValues.ending = intervalValue;
    }
    
    // Validation que l'heure de fin est après l'heure de début
    if (name === 'endTimeSlider' && newValues.beginning !== undefined) {
      if (newValues.ending <= newValues.beginning) {
        notifyError('L\'heure de fin doit être après l\'heure de début');
        return;
      }
    }
    
    // Validation que l'heure de début est avant l'heure de fin
    if (name === 'startTimeSlider' && newValues.ending !== undefined) {
      if (newValues.beginning >= newValues.ending) {
        notifyError('L\'heure de début doit être avant l\'heure de fin');
        return;
      }
    }
    
    setEditedMission(newValues);

    // Vérifier les conflits si on a une date et des heures valides
    if (editedMission.date && newValues.beginning !== undefined && newValues.ending !== undefined && newValues.beginning < newValues.ending) {
      const hasConflict = await checkTimeConflicts(
        editedMission.date, 
        newValues.beginning, 
        newValues.ending, 
        mission.id
      );
      
      if (hasConflict) {
        notifyError('Conflit d\'horaire détecté avec un autre rendez-vous');
      }
    }
  };

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
  };

  const saveChanges = async () => {

    // Validation des horaires de travail (intervalle 0=8h00, intervalle 36=17h00)
    if (editedMission.beginning < 0 || editedMission.beginning > 35) {
      notifyError('L\'heure de début doit être entre 8h00 et 16h45 !');
      return;
    }

    if (editedMission.ending < 1 || editedMission.ending > 36) {
      notifyError('L\'heure de fin doit être entre 8h15 et 17h00 !');
      return;
    }

    if (editedMission.beginning >= editedMission.ending) {
      notifyError('L\'heure de fin doit être après l\'heure de début !');
      return;
    }

    setSaving(true);
    try {
      const credentials = await getCredentials();
      
      // Vérification finale des conflits
      const hasConflict = await checkTimeConflicts(
        editedMission.date, 
        editedMission.beginning, 
        editedMission.ending, 
        mission.id
      );

      if (hasConflict) {
        notifyError('Conflit détecté avec un autre rendez-vous !');
        setSaving(false);
        return;
      }

      // Sauvegarder les modifications générales
      const updateResponse = await fetch('/rdv/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rdvId: mission.id,
          ...editedMission,
          company_name: credentials.company_name,
          company_password: credentials.company_password, 
        }),
      });

      if (!updateResponse.ok) throw new Error('Erreur lors de la sauvegarde des modifications');

      // Sauvegarder les commentaires séparément
      const commentsResponse = await fetch('/rdv/update-comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rdvId: mission.id,
          comments: comments,
          company_name: credentials.company_name,
          company_password: credentials.company_password,
        }),
      });

      if (!commentsResponse.ok) throw new Error('Erreur lors de la sauvegarde des commentaires');
      
      notifySuccess('Rendez-vous modifié avec succès !');
      
      // Mettre à jour la mission dans le state parent
      const updatedMission = {
        ...mission,
        ...editedMission,
        comments,
      };
      onUpdate(updatedMission);
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Erreur:', error);
      notifyError('Erreur lors de la sauvegarde : ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mission-popup" onClick={handleBackdropClick}>
      <div className="mission-popup-content">
        <div className="mission-popup-header">
          <h2>Détails de la Mission</h2>
        </div>
        
        <div className="mission-info-group">
          <div className="info-item">
            <span className="info-label">Client</span>
            <input
              type="text"
              name="client_name"
              value={editedMission.client_name}
              onChange={handleInputChange}
              className="info-input"
              placeholder="Nom du client"
            />
          </div>

          <div className="info-item">
            <span className="info-label">Adresse</span>
            <input
              type="text"
              name="adresse"
              value={editedMission.adresse}
              onChange={handleInputChange}
              className="info-input"
              placeholder="Adresse"
            />
          </div>

          <div className="info-item">
            <span className="info-label">N° Appartement</span>
            <input
              type="text"
              name="appartement"
              value={editedMission.appartement}
              onChange={handleInputChange}
              className="info-input"
              placeholder="Ex: Apt 42, Bât B..."
            />
          </div>

          <div className="info-item">
            <span className="info-label">Date</span>
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              locale="fr"
              dateFormat="dd/MM/yyyy"
              placeholderText="Sélectionner une date"
              className="info-input date-picker-french"
              minDate={new Date()}
              showPopperArrow={false}
              autoComplete="off"
            />
          </div>

          <div className="info-item time-slider-item">
            <span className="info-label">Heure de début</span>
            <div className="time-slider-container">
              <span className="time-label">8h00</span>
              <input
                type="range"
                name="startTimeSlider"
                value={editedMission.beginning || 0}
                onChange={handleTimeSliderChange}
                className="time-slider"
                min="0"   // Intervalle 0 = 8h00
                max="35"  // Intervalle 35 = 16h45
                step="1"  // Par intervalles de 15 minutes
                title="Glisser pour choisir l'heure de début"
              />
              <span className="time-label">16h45</span>
              <span className="time-display">{intervalToHoursFixed(editedMission.beginning || 0)}</span>
            </div>
          </div>

          <div className="info-item time-slider-item">
            <span className="info-label">Heure de fin</span>
            <div className="time-slider-container">
              <span className="time-label">8h15</span>
              <input
                type="range"
                name="endTimeSlider"
                value={editedMission.ending || 4}
                onChange={handleTimeSliderChange}
                className="time-slider"
                min="0"   // Intervalle 0 = 8h00
                max="35"  // Intervalle 36 = 17h00
                step="1"  // Par intervalles de 15 minutes
                title="Glisser pour choisir l'heure de fin"
              />
              <span className="time-label">17h00</span>
              <span className="time-display">{intervalToHoursFixed(editedMission.ending || 4)}</span>
            </div>
          </div>



          <div className="info-item">
            <span className="info-label">Opération</span>
            <select
              name="operation_id"
              value={editedMission.operation_id || ''}
              onChange={handleInputChange}
              className="info-select"
            >
              <option value="">Aucune opération</option>
              {operations.map(op => (
                <option key={op.id} value={op.id}>
                  {op.name}
                </option>
              ))}
            </select>
          </div>

          <div className="info-item comments-section">
            <span className="info-label">Commentaires</span>
            <div className="comments-container">
              <textarea
                className="comments-input"
                value={comments}
                onChange={handleCommentsChange}
                placeholder="Ajouter un commentaire..."
                rows={4}
              />
            </div>
          </div>
        </div>

        <div className="mission-popup-actions">
          <button 
            className={`save-button ${isSaving ? 'saving' : ''}`}
            onClick={saveChanges}
            disabled={isSaving}
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
          <button className="close-button" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissionPopup;
